/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import Icon from "@mui/material/Icon";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import RecordQueryView from "qqq/models/query/RecordQueryView";
import TableUtils from "qqq/utils/qqq/TableUtils";
import React from "react";


/*******************************************************************************
 ** a sort field's label: its table's label too, for a join table's field, and
 ** its name for a field the table no longer has, or that has no label.
 *******************************************************************************/
function getSortFieldLabel(tableMetaData: QTableMetaData, fieldName: string): string
{
   const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);
   if (field == null)
   {
      return (fieldName);
   }

   const fieldLabel = field.label ?? field.name ?? fieldName;
   return (fieldTable == null || fieldTable.name == tableMetaData.name ? fieldLabel : `${fieldTable.label ?? fieldTable.name}: ${fieldLabel}`);
}


/*******************************************************************************
 ** line 3 of a query history row: the sort (direction shown with the same
 ** up/down arrows as the sort control), then the visible column count.  A sort
 ** on a join-table field shows that table's label too; a field the table no
 ** longer has shows its name.
 *******************************************************************************/
export default function renderSortAndColumns(view: RecordQueryView | null, tableMetaData: QTableMetaData): JSX.Element
{
   const orderBys = view?.queryFilter?.orderBys ?? [];
   const arrowSx = {fontSize: "0.875rem !important", verticalAlign: "text-bottom", ml: "0.125rem"};

   const columnCount = view?.queryColumns?.getVisibleColumnCount();
   return (
      <>
         Sort: {orderBys.length == 0 ? "none" : orderBys.map((orderBy, index) =>
         {
            const label = getSortFieldLabel(tableMetaData, orderBy.fieldName);
            return (
               <React.Fragment key={index}>
                  {index > 0 && ", "}
                  {label}<Icon sx={arrowSx}>{orderBy.isAscending ? "arrow_upward" : "arrow_downward"}</Icon>
               </React.Fragment>
            );
         })}
         {columnCount != null && ` · ${columnCount} ${columnCount === 1 ? "column" : "columns"}`}
      </>
   );
}
